import { getPhoneCallOutcomeLabel } from "../../models/phoneCallOutcomes";
import { useMemo, useState } from "react";
import "./PerformanceWidget.css";
import PieChart from "./PieChart";
import BarChart from "./BarChart";

const PERIODS = [
	{ id: "day", label: "Today" },
	{ id: "week", label: "This week" },
	{ id: "last-week", label: "Last week" },
	{ id: "month", label: "This month" },
	{ id: "all", label: "Available history" },
];

function getPeriodRange(period, now) {
	const start = new Date(now);
	start.setHours(0, 0, 0, 0);
	if (period === "week" || period === "last-week") {
		const daysSinceMonday = (start.getDay() + 6) % 7;
		start.setDate(start.getDate() - daysSinceMonday);
	}
	if (period === "last-week") {
		start.setDate(start.getDate() - 7);
	}
	if (period === "month") {
		start.setDate(1);
	}
	const end = new Date(start);
	if (period === "day") {
		end.setDate(end.getDate() + 1);
	}
	if (period === "week" || period === "last-week") {
		end.setDate(end.getDate() + 7);
	}
	if (period === "month") {
		end.setMonth(end.getMonth() + 1);
	}
	return { start, end };
}

function PerformanceWidget({ calls = [], loading, error }){
	const [period, setPeriod] = useState("week");
	const filteredCalls = useMemo(() => {
		if (period === "all") {
			return calls;
		}
		const { start, end } = getPeriodRange(period, new Date());
		return calls.filter((call) => {
			const callDate = new Date(call?.call_at);
			return !Number.isNaN(callDate.getTime()) && callDate >= start && callDate < end;
		});
	}, [calls, period]);

	const callsPerPerson = filteredCalls.reduce((acc, call) => {
		const name = String(call?.user_name || "Unknown user");
		acc[name] = (acc[name] || 0) + 1;
		return acc;
	}, {});

	const callsPerWeekday = [1, 2, 3, 4, 5].map((weekday) => (
		filteredCalls.filter((call) => {
			const date = new Date(call?.call_at);
			return !Number.isNaN(date.getTime()) && date.getDay() === weekday;
		}).length
	));

	const colors = [
		"#00C1D5",
		"#9BDC50",
		"#E0AA74",
		"#E07474",
		"#6EA8FE",
		"#C0E673",
    ];

	const teamData = Object.entries(callsPerPerson)
		.map(([name, count], index) => ({
			label: name,
			value: count,
			color: colors[index % colors.length],
		}))
		.sort((a, b) => b.value - a.value);

	const outcomeCounts = filteredCalls.reduce((acc, call) => {
		const outcome = getPhoneCallOutcomeLabel(call?.outcome);
		acc[outcome] = (acc[outcome] || 0) + 1;
		return acc;
	}, {});
	const outcomeData = Object.entries(outcomeCounts)
		.map(([outcome, count], index) => ({ label: outcome, value: count, color: colors[index % colors.length] }))
		.sort((left, right) => right.value - left.value);

	const uniqueContacts = new Set(filteredCalls.map((call) => call?.contact_id).filter((contactId) => contactId != null)).size;
	const totalDuration = filteredCalls.reduce((sum, call) => sum + Math.max(Number(call?.duration_seconds) || 0, 0), 0);
	const averageDuration = filteredCalls.length > 0 ? Math.round(totalDuration / filteredCalls.length) : 0;
	const inboundCalls = filteredCalls.filter((call) => String(call?.direction || "").toLowerCase() === "inbound").length;
	const outboundCalls = filteredCalls.filter((call) => String(call?.direction || "").toLowerCase() === "outbound").length;

    return(
        <div className="performance-view">
			<header className="performance-toolbar">
				<div>
					<h2>Call performance</h2>
					<p>Metrics are based on the call history currently available.</p>
				</div>
				<div className="performance-periods" role="group" aria-label="Performance date range">
					{PERIODS.map((option) => (
						<button key={option.id} type="button" className={period === option.id ? "is-active" : ""} aria-pressed={period === option.id} onClick={() => setPeriod(option.id)}>
							{option.label}
						</button>
					))}
				</div>
			</header>
			<div className="performance-stats" aria-label="Call summary">
				<article aria-label={`Total calls: ${filteredCalls.length}`}><strong>{filteredCalls.length}</strong><span>Total calls</span></article>
				<article aria-label={`Contacts reached: ${uniqueContacts}`}><strong>{uniqueContacts}</strong><span>Contacts reached</span></article>
				<article aria-label={`Average duration: ${averageDuration} seconds`}><strong>{averageDuration}s</strong><span>Average duration</span></article>
				<article aria-label={`Inbound calls: ${inboundCalls}; outbound calls: ${outboundCalls}`}><strong>{inboundCalls} / {outboundCalls}</strong><span>Inbound / outbound</span></article>
			</div>
			<section className="data-charts" aria-labelledby="performance-statistics-title">
				<h2 id="performance-statistics-title">Activity breakdown</h2>
				{loading ? <p className="performance-empty">Loading call statistics...</p> : null}
				{!loading && error ? <p className="performance-empty">{error}</p> : null}
				{!loading && !error && teamData.length === 0 ? <p className="performance-empty">No calls are available for this period.</p> : null}
				{!loading && !error && teamData.length > 0 ? (
					<div className="charts-row">
						<BarChart data={teamData}/>
						<PieChart data={outcomeData} size={200}/>
					</div>
				) : null}
			</section>
			<section className="weekday-activity" aria-label="Weekday activity">
				{["Mon", "Tue", "Wed", "Thu", "Fri"].map((day, index) => (
					<div key={day}><strong>{callsPerWeekday[index]}</strong><span>{day}</span></div>
				))}
			</section>
        </div>
    );
}

export default PerformanceWidget;